import { AUTHENTICATION_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { local as localStorage } from '@packages/domain/utils/storage/local';
import { refreshToken } from 'actions';
import isEmpty from 'lodash/isEmpty';
import type { RootState } from 'reducers/types';
import type { AnyAction, Middleware, MiddlewareAPI } from 'redux';
import { getRefreshToken, isLoggedIn } from 'selectors';
import type { PayexDispatch } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';
import { date } from 'utils/date/date';
import {
  AUTH_LOCALSTORAGE_KEY,
  IMPERSONATE_USER_SUCCESS,
  LOGIN_EXPIRATION_IN_MINUTES,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER_REQUEST,
  LOGOUT_USER_SUCCESS,
  REFRESH_TOKEN,
} from '../../../constants';

type AccessToken = {
  accessToken: string;
  idleTimeout: string;
};

type Store = MiddlewareAPI<PayexDispatch, RootState>;

const Authentication: Middleware<unknown, RootState> =
  store => next => async action => {
    const accessToken = getUserAuthentication();

    if (isLoginSuccess(action)) {
      setAccessToken(action);
    }
    if (isLoggedInOnAnotherTab(store, action)) {
      // @ts-expect-error for some reason we cannot type the action
      store.dispatch(refreshToken());
    }

    updateExpiration(accessToken);
    return next(action);
  };

const getUserAuthentication = () => {
  const userAuthentication = localStorage.getItem(AUTH_LOCALSTORAGE_KEY);

  if (isEmpty(userAuthentication)) {
    return {};
  }

  return JSON.parse(userAuthentication as string);
};

const expiration = () => {
  return date.future(LOGIN_EXPIRATION_IN_MINUTES, date.MINUTES);
};

const setAccessToken = (action: AnyAction) => {
  const {
    payload: { accessToken },
  } = action;
  const token = JSON.stringify({
    accessToken,
    idleTimeout: expiration(),
  });

  localStorage.setItem(AUTH_LOCALSTORAGE_KEY, token);
};

const isExpired = (accessToken?: AccessToken) => {
  if (isEmpty(accessToken)) {
    return false;
  }
  const currentDate = date.now();
  const expirationDate = accessToken.idleTimeout;
  return date.isBefore(expirationDate, currentDate);
};

const isLoginSuccess = (action: AnyAction) =>
  action.type === (LOGIN_USER_SUCCESS || IMPERSONATE_USER_SUCCESS);

const updateExpiration = (accessToken?: AccessToken) => {
  if (!isEmpty(accessToken)) {
    accessToken.idleTimeout = expiration();
    localStorage.setItem(AUTH_LOCALSTORAGE_KEY, JSON.stringify(accessToken));
  }
};

const hasBeenLoggedOutPreviously = (store: Store, action: AnyAction) => {
  if (action.type === LOGOUT_USER_SUCCESS) {
    return false;
  }
  if (action.type === LOGOUT_USER_REQUEST) {
    return false;
  }

  const state = store.getState();

  const hasStoredRefreshToken = cookies.read(AUTHENTICATION_COOKIE_NAME);
  const hasRefreshToken = getRefreshToken(state);

  return !!(!hasStoredRefreshToken && hasRefreshToken);
};

const _shouldLogOut = (
  store: Store,
  action: AnyAction,
  accessToken: AccessToken,
) => {
  return (
    hasBeenLoggedOutPreviously(store, action) ||
    (!isLogoutSuccess(action) && isExpired(accessToken))
  );
};

const isLoggedInOnAnotherTab = (store: Store, action: AnyAction) => {
  if (action.type === REFRESH_TOKEN) {
    return false;
  }

  const state = store.getState();
  const hasRefreshToken = getRefreshToken(state);
  const isNotLoggedIn = !isLoggedIn(state);

  return hasRefreshToken && isNotLoggedIn;
};

const isLogoutSuccess = ({ type }: { type: string }) => {
  const logoutActions = [LOGOUT_USER_SUCCESS, LOGOUT_USER_REQUEST];
  return logoutActions.includes(type);
};

export { Authentication };
